import {
  Component,
  OnInit,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  AfterViewInit,
  HostListener,
} from '@angular/core';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Store } from '@ngrx/store';
import * as fromRoot from './state/app.selectors';
import * as fromAuth from '../screens/login/state/login.selectors';
import * as lgeApp from './state/app.actions';
import * as AuthenticationActions from '../screens/login/state/login.actions';
import { MediaMatcher } from '@angular/cdk/layout';
import {
  IUserAuth,
  PermissionsService,
  IExternalData,
  AuthenticationService,
  IUser,
  LGEDataService,
} from 'src/commons';
import { Observable, of, Subscription } from 'rxjs';
import { PreviousRouteService } from 'src/commons/services/previousRoute.service';
import { AngularMaterialService } from 'src/commons/utilities/angular.material.service';
import { catchError, filter, map, skipWhile, tap } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { ILoading } from 'src/commons/interfaces/screens.interface';
import {
  ActivatedRoute,
  Router,
  RouterEvent,
  NavigationStart,
  NavigationEnd,
  NavigationCancel,
  NavigationError,
  Scroll,
} from '@angular/router';
import { ViewportScroller } from '@angular/common';

@Component({
  selector: 'lge-app',
  templateUrl: '././app.component.html',
  styleUrls: ['././app.component.scss'],
  animations: [
    trigger('splashAnimate', [
      state(
        'true',
        style({ opacity: 1, position: 'fixed', 'z-index': '1000' })
      ),
      state(
        'false',
        style({ opacity: 0, position: 'absolute', 'z-index': '-1' })
      ),
      transition('false <=> true', animate(20)),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() private showSidenav$: Observable<boolean>;
  private showSidenavSubscription: Subscription;
  showSidenav: boolean;
  @Input() private loggedIn$: Observable<boolean>;
  private loggedInSubscription: Subscription;
  loggedIn: boolean;
  @Input() private user$: Observable<IUserAuth>;
  private userSubscription: Subscription;
  user: IUserAuth;
  @Input() private splashScreen$: Observable<ILoading>;
  private splashScreenSubscription: Subscription;
  splashScreen: ILoading = {
    isLoading: true,
    loadingMessage: '',
  };
  @Input() private externalProfileData$: Observable<IExternalData | null>;
  private externalProfileDataSubscription: Subscription;
  externalProfileData: IExternalData = null;
  mobileQuery: MediaQueryList;
  mobileQueryListener: () => void;

  isAdmin = 'ADMIN';
  isApprover = 'APPROVER';
  isReviewer = 'REVIEWER';
  isProfileExpired = true;
  sessionCheck: any;
  isTryAgain = false;
  isExpanded = true;
  spashMessage: string = '';
  screenName: string = '';

  @HostListener('window:unload', ['$event'])
  scrollPosition: [number, number] = [0, 0];
  unloadHandler(event) {
    window.sessionStorage.clear();
  }
  constructor(
    private store: Store<fromRoot.AppState>,
    private ref: ChangeDetectorRef,
    private media: MediaMatcher,
    private route: ActivatedRoute,
    private router: Router,
    private ams: AngularMaterialService,
    private _dataService: LGEDataService,
    private authService: AuthenticationService,
    private permissionsService: PermissionsService,
    private spinner: NgxSpinnerService
  ) {
    this.selectState();
    this.mobileQuery = this.media.matchMedia('(max-width: 959px)'); //fxFlex md max-width = 959
    this.mobileQueryListener = () => this.ref.detectChanges();
    this.mobileQuery.addListener(this.mobileQueryListener);
    router.events
      .pipe(filter((e) => e instanceof RouterEvent))
      .subscribe((event: RouterEvent) => {
        switch (true) {
          case event instanceof NavigationStart: {
            store.dispatch(new lgeApp.UpdateSplashScreenStatus(true, ''));
            break;
          }
          case event instanceof NavigationEnd: {
            store.dispatch(new lgeApp.UpdateSplashScreenStatus(false));
            // scroll back to top of container
            const contentContainer = document.querySelector(
              '.mat-sidenav-content'
            );
            contentContainer.scrollTop = 0;
            if (
              Object.keys(this.route.snapshot.firstChild.data).includes(
                'screenName'
              )
            )
              this.screenName =
                this.route.snapshot.firstChild.data['screenName'];
            break;
          }
          case event instanceof NavigationCancel:
          case event instanceof NavigationError: {
            store.dispatch(new lgeApp.UpdateSplashScreenStatus(false));
            break;
          }
          default: {
            break;
          }
        }
      });
  }

  ngOnInit() {
    this.startSubscriptions();
  }

  ngAfterViewInit() {
    const store = this.store;
  }

  keepMenuOpen(isMobile: boolean): boolean {
    if (isMobile) {
      return this.showSidenav;
    } else {
      return true;
    }
  }

  private selectState(): void {
    this.showSidenav$ = this.store.select(fromRoot.getShowSidenav);
    this.loggedIn$ = this.store.select(fromAuth.getLoggedIn);
    this.user$ = this.store
      .select(fromAuth.getUser)
      .pipe(skipWhile((user) => user === null || user === undefined));
    this.splashScreen$ = this.store.select(fromRoot.getSplashScreenStatus);
    this.externalProfileData$ = this.store
      .select(fromAuth.getExternalProfile)
      .pipe(skipWhile((profile) => profile === null || profile === undefined));
  }

  private startSubscriptions(): void {
    this.showSidenavSubscription = this.showSidenav$.subscribe((data) => {
      this.showSidenav = data;
      this.isExpanded = this.mobileQuery.matches || !this.isExpanded;
      this.ref.markForCheck();
    });
    this.loggedInSubscription = this.loggedIn$.subscribe((data) => {
      // this.loggedIn = data;
      this.ref.markForCheck();
    });
    this.userSubscription = this.user$.subscribe((data) => {
      if (data !== null && data !== undefined) {
        this.user = data;
        if (
          this.user.applicationID === 'CCMSEXT' &&
          (this.externalProfileData === null ||
            this.externalProfileData.externalUser.email !== this.user.userId)
        ) {
          this.store.dispatch(
            new AuthenticationActions.LoadExternalProfileState()
          );
        }
        this.store.dispatch(new AuthenticationActions.LoadFormConfigState());
        this.ref.markForCheck();
        this.loggedIn = true;
      } else this.loggedIn = false;
    });
    this.splashScreenSubscription = this.splashScreen$.subscribe((data) => {
      this.splashScreen = data;
      if (data.isLoading) this.spinner.show();
      else this.spinner.hide();
    });
    this.externalProfileDataSubscription = this.externalProfileData$.subscribe(
      (data) => {
        this.externalProfileData = data;
        console.log(this.externalProfileData);
        if (
          this.externalProfileData !== null &&
          this.externalProfileData !== undefined
        ) {
          this.isProfileExpired =
            this.externalProfileData.externalUser.isVerified !== 1;
          if(this.isProfileExpired)
            this.verifyEIN(this.externalProfileData.orgInfoExt.EIN);
          if (data) {
            this.sessionCheck = setInterval(
              this.checkSession.bind(this),
              300000
            );
          } else {
            clearInterval(this.sessionCheck);
          }
        }
        this.ref.markForCheck();
      }
    );
  }
  private checkSession(): void {
    this.authService.validateSession(this.authService.currentSession).pipe(
      map((res) => {
        clearInterval(this.sessionCheck);
        this.isTryAgain = false;
        this.sessionCheck = setInterval(this.sessionCheck.bind(this), 600000); // check session every 10 mins
      }),
      catchError((err: Error) => {
        clearInterval(this.sessionCheck);
        if (this.user) {
          this.ams.closeDialog();
          this.ams.showAlert(
            'Session Timeout',
            'Your session has been expired due to inactivity.  Please click OK to log in again.'
          );
          this.ams.alert.afterClosed().subscribe(() => {
            this.ams.showLogin(this.isTryAgain);
            this.ams.login.afterClosed().subscribe((isLogin) => {
              if (isLogin) {
                this.isTryAgain = true;
                this.sessionCheck = setInterval(
                  this.checkSession.bind(this),
                  1000
                );
              }
            });
          });
        }
        return of(err);
      })
    );
  }
  hasRole(role: string) {
    return this.permissionsService.hasRoles(role);
  }

  isInternal(): boolean {
    return this.permissionsService.isInternal();
  }
  closeSidenav(): void {
    this.store.dispatch(new lgeApp.CloseSidenav());
  }
  openSidenav(): void {
    this.store.dispatch(new lgeApp.OpenSidenav());
  }

  private endSubscriptions(): void {
    clearInterval(this.sessionCheck);
    this.showSidenavSubscription.unsubscribe();
    this.loggedInSubscription.unsubscribe();
    this.userSubscription.unsubscribe();
    this.splashScreenSubscription.unsubscribe();
    this.externalProfileDataSubscription.unsubscribe();
  }

  private verifyEIN(ein: string) {
    this.ams.showLoader('Verifying EIN and Organization Info ...')
    this._dataService.CharityCheck(ein).subscribe({
      next: (result) => {
        if (result === 'invalid' || result === 'Expired') {
          this.ams.closeDialog();
          this.ams.showAlert(
            'Unable to verify!',
            'Your organization information and tax-exempt status cannot be verified. Please contact contributions@lge-ku.com for further support.'
          );
          this.ref.markForCheck();
          this.ams.alert.afterClosed().subscribe((result: string) => {
            let externalData = JSON.parse(
              JSON.stringify(this.externalProfileData)
            );
            if (externalData.externalUser.accessStatus !== 'pendingEIN') {
              externalData.externalUser.accessStatus = 'pendingEIN';
              externalData.externalUser.isVerified =
                result === 'invalid' ? 0 : 2;
              // this.ams.showLoader('Loading...');
              this._dataService.externalProfileEditDetails(externalData).pipe(
                tap((result: any) => {
                  this.store.dispatch(
                    new AuthenticationActions.LoadExternalProfileState()
                  );
                  setTimeout(() => {
                    this.ams.closeDialog();
                    this.ref.markForCheck();
                  }, 100);
                })
              );
            }
          });
        } else {
          let externalData = JSON.parse(
            JSON.stringify(this.externalProfileData)
          );
          this.isProfileExpired = false;
          externalData.externalUser.isVerified = 1;
          externalData.orgInfoExt.name = result.organization_name;
          externalData.orgInfoExt.subsection_501 = result.subsection_501;
          // this.ams.showLoader('Loading...');
          this._dataService.externalProfileEditDetails(externalData).pipe(
            tap(() => {
              this.store.dispatch(
                new AuthenticationActions.LoadExternalProfileState()
              );
              setTimeout(() => {
                this.ams.closeDialog();
                this.ref.markForCheck();
              }, 100);
            })
          );
        }
      },
      error: (error) => {
        this.isProfileExpired == true;
        if (
          error.message.includes('invalid') ||
          error.message.includes('Expired')
        ) {
          this.ams.closeDialog();
          this.ams.showAlert(
            'Unable to verify!',
            'Your organization information and tax-exempt status cannot be verified. Please contact contributions@lge-ku.com with questions.'
          );
          this.ref.markForCheck();
          this.ams.alert.afterClosed().subscribe((result: any) => {
            let externalData = JSON.parse(
              JSON.stringify(this.externalProfileData)
            );
            if (externalData.externalUser.accessStatus !== 'pendingEIN') {
              externalData.externalUser.accessStatus = 'pendingEIN';
              externalData.externalUser.isVerified =
                result === 'invalid' ? 0 : 2;
              // this.ams.showLoader('Loading...');
              this._dataService.externalProfileEditDetails(externalData).pipe(
                tap((result: any) => {
                  this.store.dispatch(
                    new AuthenticationActions.LoadExternalProfileState()
                  );
                  setTimeout(() => {
                    this.ams.closeDialog();
                    this.ref.markForCheck();
                  }, 100);
                })
              );
            }
          });
        } else {
          this.ams.closeDialog();
          this.ams.showAlert(
            'Unable to verify!',
            `Please contact contributions@lge-ku.com with questions.\n ${error.message}`
          );
          this.ref.markForCheck();
        }
        return of(error);
      },
    });
  }
  logout(): void {
    this.store.dispatch(new AuthenticationActions.Logout());
  }

  ngOnDestroy() {
    this.endSubscriptions();
    this.mobileQuery.removeListener(this.mobileQueryListener);
  }
}
